import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors, fonts } from "../theme";
import { View, Text, Card, Button, Press, ScreenHeader } from "../ui";
import { Icon } from "../Icon";
import { useI18n } from "../i18n";
import { useAppState } from "../state";
import { ORDER_ITEMS, shuffle, t } from "../content";

export default function Order() {
  const navigate = useNavigate();
  const { tr, lang } = useI18n();
  const { markDone } = useAppState();
  const item = ORDER_ITEMS[0];
  const shuffledSteps = useMemo(() => shuffle(item.steps.map((s, i) => ({ s, correct: i }))), []); // eslint-disable-line
  const [seq, setSeq] = useState([]); // indices into shuffledSteps
  const [checked, setChecked] = useState(false);

  const tap = (i) => { if (checked || seq.includes(i)) return; setSeq([...seq, i]); };
  const reset = () => { setSeq([]); setChecked(false); };
  const isCorrect = seq.length === item.steps.length && seq.every((si, pos) => shuffledSteps[si].correct === pos);

  return (
    <View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surfaceSecondary }}>
      <ScreenHeader title={tr("orderIt")} onBack={() => navigate(-1)} />
      <View style={{ padding: 20 }}>
        <Text style={{ fontWeight: fonts.regular, fontSize: 14, color: colors.muted, marginBottom: 12 }}>{tr("tapInOrder")}</Text>
        <Text style={{ fontWeight: fonts.semibold, fontSize: 15, color: colors.onSurface, marginBottom: 16 }}>{t(item.prompt, lang)}</Text>

        <View style={{ gap: 10, marginBottom: 20 }}>
          {seq.map((si, pos) => {
            const ok = shuffledSteps[si].correct === pos;
            return (
              <Card key={pos} style={{ flexDirection: "row", alignItems: "center", gap: 12, backgroundColor: checked ? (ok ? colors.successSurface : colors.errorSurface) : colors.brandTertiary, border: `1.5px solid ${checked ? (ok ? colors.success : colors.error) : colors.brandPrimary}` }}>
                <View style={{ width: 26, height: 26, borderRadius: 13, backgroundColor: colors.brandPrimary, alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Text style={{ fontWeight: fonts.bold, fontSize: 13, color: colors.onBrandPrimary }}>{pos + 1}</Text>
                </View>
                <Text style={{ flex: 1, fontWeight: fonts.medium, fontSize: 14, color: colors.onSurface }}>{t(shuffledSteps[si].s, lang)}</Text>
              </Card>
            );
          })}
        </View>

        <View style={{ gap: 10 }}>
          {shuffledSteps.map((st, i) => {
            const used = seq.includes(i);
            if (used) return null;
            return (
              <Press key={i} testID={`order-step-${i}`} onClick={() => tap(i)}>
                <Card style={{ padding: 14 }}>
                  <Text style={{ fontWeight: fonts.medium, fontSize: 14, color: colors.onSurface }}>{t(st.s, lang)}</Text>
                </Card>
              </Press>
            );
          })}
        </View>

        <View style={{ marginTop: 20, gap: 12 }}>
          {!checked && seq.length === item.steps.length && (
            <Button testID="order-check" title={tr("checkOrder")} icon="check" onPress={() => { setChecked(true); if (isCorrect) markDone("orders", item.id); }} />
          )}
          {checked && (
            <Card style={{ alignItems: "center", padding: 20, backgroundColor: isCorrect ? colors.successSurface : colors.errorSurface }}>
              <Icon name={isCorrect ? "check-circle" : "x-circle"} size={40} color={isCorrect ? colors.success : colors.error} weight="fill" />
              <Text style={{ fontWeight: fonts.bold, fontSize: 18, color: colors.onSurface, marginTop: 8 }}>{isCorrect ? tr("correct") : tr("incorrect")}</Text>
            </Card>
          )}
          {(checked || seq.length > 0) && <Button title={checked && isCorrect ? tr("done") : tr("retry")} variant="secondary" icon="shuffle" onPress={() => (checked && isCorrect ? navigate(-1) : reset())} />}
        </View>
      </View>
    </View>
  );
}
